/* eslint-disable sonarjs/cognitive-complexity */
import chartSquareBar from '@iconify/icons-heroicons-outline/chart-square-bar';
import documentDuplicate from '@iconify/icons-heroicons-outline/document-duplicate';
import newspaper from '@iconify/icons-heroicons-outline/newspaper';
import officeBuilding from '@iconify/icons-heroicons-outline/office-building';
import questionMarkCircle from '@iconify/icons-heroicons-outline/question-mark-circle';
import trendingUp from '@iconify/icons-heroicons-outline/trending-up';
import userGroup from '@iconify/icons-heroicons-outline/user-group';
import { useMemo } from 'react';

import { RolesWithProduct } from '@customTypes/api/Commons';
import SidebarConfig from '@customTypes/SidebarConfig';
import pathnames from '@src/app/routes/pathnames';
import FEATURE_FLAGS from '@src/constants/featureFlags';
import { useAuthorizationContext } from '@src/context/AuthorizationContext';

const useSidebarConfig = () => {
  const { isAllowed, rolesProduct } = useAuthorizationContext();

  const AVAILABILITY_MAP = useMemo(() => {
    const isPBF = rolesProduct === RolesWithProduct['SPV.PBF'];
    return {
      PERFORMANCE: {
        SPV_DASHBOARD: isAllowed('spv-dashboard') && !isPBF,
        ARE_PERFORMANCE: isAllowed('are-performance') && !isPBF,
        AGENT_PERFORMANCE: isAllowed('agent-performance.this-month') && !isPBF,
        KPI_INCENTIVE: isAllowed('kpi-incentive') && !isPBF,
      },

      BRANCH_ACTIVITY: {
        WEEKLY_PLANNING: {
          RECRUITMENT:
            (isAllowed('recruitment.recruitment-area') ||
              isAllowed('recruitment.recruitment-list')) &&
            !isPBF,
          MAINTAIN:
            isAllowed('maintain.assignment') ||
            isAllowed('maintain.are-schedule'),
        },
        JOIN_VISIT:
          (isAllowed('join-visit.this-month') ||
            isAllowed('join-visit.historical')) &&
          !isPBF,
        '1ON1_SESSION': {
          SPV_ARE:
            (isAllowed('1-on-1.sales') ||
              isAllowed('1-on-1.maintain') ||
              isAllowed('1-on-1.recruit')) &&
            !isPBF,
          BM_SPV:
            (isAllowed('1-on-1-bm.sales') ||
              isAllowed('1-on-1-bm.maintain') ||
              isAllowed('1-on-1-bm.recruit')) &&
            FEATURE_FLAGS.features.oneOnOneBmSpv(),
        },
        WEEKLY_MEETING:
          FEATURE_FLAGS.routes.weeklyMeeting() &&
          isAllowed('weekly-meeting') &&
          !isPBF,
      },

      REPORT: {
        MAINTAIN: isAllowed('report.maintain'),
        RECRUIT: isAllowed('report.recruit') && !isPBF,
      },

      TEAM_MANAGEMENT: {
        AGENT_MANAGEMENT: isAllowed('agent-management') && !isPBF,
        ARE_HIERARCHY: isAllowed('hierarchy') && !isPBF,
      },

      LEADS_TRACKER: isAllowed('leads-tracker') && !isPBF,

      AGENCY_NEWS: {
        UPDATES: isAllowed('agency-news.updates') && !isPBF,
        PROMO_MATERIAL:
          FEATURE_FLAGS.routes.agencyNewsPromoMaterial() &&
          isAllowed('agency-news.promo-material') &&
          !isPBF,
        LOYALTY_PROGRAM:
          FEATURE_FLAGS.routes.agencyNewsLoyaltyProgram() &&
          isAllowed('agency-news.loyalty-program') &&
          !isPBF,
        TRAINING_MATERIAL:
          FEATURE_FLAGS.routes.agencyNewsTrainingMaterial() &&
          isAllowed('agency-news.training-material') &&
          !isPBF,
      },

      USER_GUIDE: true,
    };
  }, [isAllowed, rolesProduct]);

  const configs: SidebarConfig[] = useMemo(
    () => [
      {
        label: 'sidebar.performanceDashboard',
        isAvailable:
          AVAILABILITY_MAP.PERFORMANCE.SPV_DASHBOARD ||
          AVAILABILITY_MAP.PERFORMANCE.ARE_PERFORMANCE ||
          AVAILABILITY_MAP.PERFORMANCE.AGENT_PERFORMANCE,
        icon: chartSquareBar,
        children: [
          {
            label: 'sidebar.spvDashboard',
            isAvailable: AVAILABILITY_MAP.PERFORMANCE.SPV_DASHBOARD,
            path: pathnames.home(),
          },
          {
            label: 'sidebar.arePerformance',
            isAvailable: AVAILABILITY_MAP.PERFORMANCE.ARE_PERFORMANCE,
            path: pathnames.arePerformance(),
          },
          {
            label: 'sidebar.agentPerformance',
            isAvailable: AVAILABILITY_MAP.PERFORMANCE.AGENT_PERFORMANCE,
            path: pathnames.agentPerformance(),
          },
          {
            label: 'sidebar.kpiIncentive',
            isAvailable: AVAILABILITY_MAP.PERFORMANCE.KPI_INCENTIVE,
            path: pathnames.kpiIncentive(),
          },
        ],
      },
      {
        label: 'sidebar.branchActivity',
        isAvailable:
          AVAILABILITY_MAP.BRANCH_ACTIVITY.WEEKLY_PLANNING.RECRUITMENT ||
          AVAILABILITY_MAP.BRANCH_ACTIVITY.WEEKLY_PLANNING.MAINTAIN ||
          AVAILABILITY_MAP.BRANCH_ACTIVITY.JOIN_VISIT ||
          AVAILABILITY_MAP.BRANCH_ACTIVITY['1ON1_SESSION'].SPV_ARE ||
          AVAILABILITY_MAP.BRANCH_ACTIVITY['1ON1_SESSION'].BM_SPV,
        icon: officeBuilding,
        children: [
          {
            label: 'sidebar.weeklyPlanning',
            isAvailable:
              AVAILABILITY_MAP.BRANCH_ACTIVITY.WEEKLY_PLANNING.RECRUITMENT ||
              AVAILABILITY_MAP.BRANCH_ACTIVITY.WEEKLY_PLANNING.MAINTAIN,
            children: [
              {
                label: 'sidebar.recruitment',
                isAvailable:
                  AVAILABILITY_MAP.BRANCH_ACTIVITY.WEEKLY_PLANNING.RECRUITMENT,
                path: pathnames.recruitActivity(),
              },
              {
                label: 'sidebar.maintain',
                isAvailable:
                  AVAILABILITY_MAP.BRANCH_ACTIVITY.WEEKLY_PLANNING.MAINTAIN,
                path: pathnames.maintainActivity(),
              },
            ],
          },
          {
            label: 'sidebar.weeklyMeeting',
            isAvailable: AVAILABILITY_MAP.BRANCH_ACTIVITY.WEEKLY_MEETING,
            path: pathnames.weeklyMeeting(),
          },
          {
            label: 'sidebar.joinVisit',
            isAvailable: AVAILABILITY_MAP.BRANCH_ACTIVITY.JOIN_VISIT,
            path: pathnames.joinVisit(),
          },
          {
            label: 'sidebar.1on1session',
            isAvailable:
              AVAILABILITY_MAP.BRANCH_ACTIVITY['1ON1_SESSION'].SPV_ARE ||
              AVAILABILITY_MAP.BRANCH_ACTIVITY['1ON1_SESSION'].BM_SPV,
            children: [
              {
                label: 'sidebar.spvAre1on1',
                isAvailable:
                  AVAILABILITY_MAP.BRANCH_ACTIVITY['1ON1_SESSION'].SPV_ARE,
                path: pathnames.oneOnOne(),
              },
              {
                label: 'sidebar.bmSpv1on1',
                isAvailable:
                  AVAILABILITY_MAP.BRANCH_ACTIVITY['1ON1_SESSION'].BM_SPV,
                path: pathnames.oneOnOne(),
              },
            ],
          },
        ],
      },
      {
        label: 'sidebar.report',
        isAvailable:
          AVAILABILITY_MAP.REPORT.MAINTAIN || AVAILABILITY_MAP.REPORT.RECRUIT,
        icon: documentDuplicate,
        children: [
          {
            label: 'sidebar.maintainReport',
            isAvailable: AVAILABILITY_MAP.REPORT.MAINTAIN,
            path: pathnames.maintainReport(),
          },
          {
            label: 'sidebar.recruitReport',
            isAvailable: AVAILABILITY_MAP.REPORT.RECRUIT,
            path: pathnames.recruitReport(),
          },
        ],
      },
      {
        label: 'sidebar.teamManagement',
        isAvailable:
          AVAILABILITY_MAP.TEAM_MANAGEMENT.AGENT_MANAGEMENT ||
          AVAILABILITY_MAP.TEAM_MANAGEMENT.ARE_HIERARCHY,
        icon: userGroup,
        children: [
          {
            label: 'sidebar.agentManagement',
            isAvailable: AVAILABILITY_MAP.TEAM_MANAGEMENT.AGENT_MANAGEMENT,
            path: pathnames.agentManagement(),
          },
          {
            label: 'sidebar.areHierarchy',
            isAvailable: AVAILABILITY_MAP.TEAM_MANAGEMENT.ARE_HIERARCHY,
            path: pathnames.teamHierarchy(),
          },
        ],
      },
      {
        label: 'sidebar.leadsTracker',
        isAvailable: AVAILABILITY_MAP.LEADS_TRACKER,
        icon: trendingUp,
        path: pathnames.leadsTracker(),
      },
      {
        label: 'sidebar.agencyNews',
        isAvailable: AVAILABILITY_MAP.AGENCY_NEWS.UPDATES,
        icon: newspaper,
        children: [
          {
            label: 'sidebar.updates',
            isAvailable: AVAILABILITY_MAP.AGENCY_NEWS.UPDATES,
            path: pathnames.agencyNewsUpdates(),
          },
          {
            label: 'sidebar.promoMaterial',
            isAvailable: AVAILABILITY_MAP.AGENCY_NEWS.PROMO_MATERIAL,
            path: pathnames.agencyNewsPromoMaterial(),
          },
          {
            label: 'sidebar.loyaltyProgram',
            isAvailable: AVAILABILITY_MAP.AGENCY_NEWS.LOYALTY_PROGRAM,
            path: pathnames.agencyNewsLoyaltyProgram(),
          },
          {
            label: 'sidebar.trainingMaterial',
            isAvailable: AVAILABILITY_MAP.AGENCY_NEWS.TRAINING_MATERIAL,
            path: pathnames.agencyNewsTrainingMaterial(),
          },
        ],
      },
      {
        label: 'sidebar.userGuide',
        isAvailable: AVAILABILITY_MAP.USER_GUIDE,
        icon: questionMarkCircle,
        path: pathnames.userGuide(),
      },
    ],
    [
      AVAILABILITY_MAP.AGENCY_NEWS.LOYALTY_PROGRAM,
      AVAILABILITY_MAP.AGENCY_NEWS.PROMO_MATERIAL,
      AVAILABILITY_MAP.AGENCY_NEWS.TRAINING_MATERIAL,
      AVAILABILITY_MAP.AGENCY_NEWS.UPDATES,
      AVAILABILITY_MAP.BRANCH_ACTIVITY,
      AVAILABILITY_MAP.LEADS_TRACKER,
      AVAILABILITY_MAP.PERFORMANCE.AGENT_PERFORMANCE,
      AVAILABILITY_MAP.PERFORMANCE.ARE_PERFORMANCE,
      AVAILABILITY_MAP.PERFORMANCE.KPI_INCENTIVE,
      AVAILABILITY_MAP.PERFORMANCE.SPV_DASHBOARD,
      AVAILABILITY_MAP.REPORT.MAINTAIN,
      AVAILABILITY_MAP.REPORT.RECRUIT,
      AVAILABILITY_MAP.TEAM_MANAGEMENT.AGENT_MANAGEMENT,
      AVAILABILITY_MAP.TEAM_MANAGEMENT.ARE_HIERARCHY,
      AVAILABILITY_MAP.USER_GUIDE,
    ],
  );

  return configs;
};

export default useSidebarConfig;
